import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { DynamicThemeContext } from '@guryou/html-components';
import { stateIsLoaded } from '@guryou/redux';
import {
  clearFeelThemePrimaryFromSession,
  readFeelThemePrimaryFromSession,
  readWidgetAuthBrandingFromSession,
  writeFeelThemePrimaryToSession,
} from '../constants/widgetIntegrationBranding';

const FeelPanelBrandingContext = createContext(null);

function FeelPanelBrandingProvider({ children }) {
  const { setMainColor } = useContext(DynamicThemeContext);
  const location = useLocation();
  const [panelBranding, setPanelBrandingState] = useState(() => readWidgetAuthBrandingFromSession());
  const lastAppliedThemeHexRef = useRef(readFeelThemePrimaryFromSession());

  const auth = useSelector(s => s.auth);
  const authLoaded = stateIsLoaded(auth);
  const isLoggedIn = authLoaded && !!auth?.data;
  const isProviderOrBookRoute = useMemo(() => /^\/(en|it)\/provider\/\d+/.test(location.pathname) || location.pathname.startsWith('/book'), [
    location.pathname,
  ]);

  const resolvedPanelBranding = useMemo(() => {
    if (isProviderOrBookRoute) {
      return null;
    }
    if (authLoaded && !isLoggedIn) {
      return null;
    }
    let s = null;
    try {
      s = readWidgetAuthBrandingFromSession();
    } catch (_) {
      s = null;
    }
    if (!s) {
      return null;
    }
    const p = panelBranding;
    if (!p) {
      return s;
    }
    return {
      logoUrl: p.logoUrl ?? s.logoUrl ?? null,
      brandName: p.brandName || s.brandName || '',
      accentColor: p.accentColor ?? s.accentColor ?? null,
    };
  }, [panelBranding, authLoaded, isLoggedIn, isProviderOrBookRoute]);

  const resolvedStableRef = useRef(undefined);
  const resolvedPanelBrandingStable = useMemo(() => {
    const next = resolvedPanelBranding;
    const prev = resolvedStableRef.current;
    if (
      next &&
      prev &&
      typeof prev === 'object' &&
      prev.accentColor === next.accentColor &&
      prev.logoUrl === next.logoUrl &&
      prev.brandName === next.brandName
    ) {
      return prev;
    }
    if (next === null && prev === null) {
      return prev;
    }
    resolvedStableRef.current = next;
    return next;
  }, [resolvedPanelBranding]);

  useEffect(() => {
    if (isProviderOrBookRoute) {
      setPanelBrandingState(null);
      return;
    }
    if (!authLoaded) {
      return;
    }
    if (!isLoggedIn) {
      setPanelBrandingState(null);
      return;
    }
    setPanelBrandingState(prev => {
      let fromSession = null;
      try {
        fromSession = readWidgetAuthBrandingFromSession();
      } catch (_) {
        fromSession = null;
      }
      if (!fromSession) {
        return null;
      }
      return prev ?? fromSession;
    });
  }, [authLoaded, isLoggedIn, isProviderOrBookRoute]);

  const accentForTheme = resolvedPanelBrandingStable?.accentColor;

  useLayoutEffect(() => {
    if (accentForTheme) {
      const v = accentForTheme.replace(/^#/, '');
      if (lastAppliedThemeHexRef.current !== v) {
        lastAppliedThemeHexRef.current = v;
        writeFeelThemePrimaryToSession(v);
        setMainColor(v);
      }
      return;
    }
    if (!authLoaded) {
      return;
    }
    lastAppliedThemeHexRef.current = null;
    clearFeelThemePrimaryFromSession();
    setMainColor(null);
  }, [accentForTheme, authLoaded, setMainColor]);

  const setPanelBranding = useCallback(next => {
    setPanelBrandingState(next);
  }, []);

  const value = useMemo(() => ({ panelBranding, resolvedPanelBranding: resolvedPanelBrandingStable, setPanelBranding }), [
    panelBranding,
    resolvedPanelBrandingStable,
    setPanelBranding,
  ]);

  return <FeelPanelBrandingContext.Provider value={value}>{children}</FeelPanelBrandingContext.Provider>;
}

function useFeelPanelBranding() {
  return (
    useContext(FeelPanelBrandingContext) || {
      panelBranding: null,
      resolvedPanelBranding: null,
      setPanelBranding: () => {},
    }
  );
}

export { FeelPanelBrandingProvider, useFeelPanelBranding };
